/* ============================================================
   Orb.jsx — WebGL shader orb (OGL), adapted from the reactbits
   "Orb" for our no-bundler setup. Registers window.Orb.

   - OGL is loaded at runtime via dynamic import() from a CDN
     (no build step / import maps needed).
   - If WebGL or the import fails, it falls back to a soft CSS glow
     so the layout never breaks.

   Load AFTER React + Babel:
     <script type="text/babel" src="Orb.jsx"></script>

   Usage (fills its parent — give the parent/this a size):
     <Orb />
     <Orb hue={40} forceHoverState />
     <Orb hue={0} hoverIntensity={0.3} backgroundColor="#0a0a0a" />

   Props (match the reference):
     hue            number   color rotation in degrees (default 0)
     hoverIntensity number   pointer distortion strength (default 0.2)
     rotateOnHover  boolean  spin while hovered (default true)
     forceHoverState boolean keep the hover look always on (default false)
     backgroundColor string  hex/rgb/hsl that the shader blends against
     size           number   optional fixed px size (else fills parent)
     className,style          passthrough on the container
   ============================================================ */

(function () {
  const OGL_URL = "https://esm.sh/ogl@1.0.11";
  // Load the ESM at runtime WITHOUT Babel rewriting the dynamic import()
  // (text/babel scripts get transpiled, which would otherwise break import()).
  const dynamicImport = new Function("u", "return import(u)");

  const ORB_CSS = `
    .orb-container { position: relative; width: 100%; height: 100%; }
    .orb-container canvas { display: block; width: 100%; height: 100%; }
    .orb-container.orb-fallback {
      border-radius: 50%;
      background:
        radial-gradient(circle at 50% 44%,
          rgba(254,229,0,.72) 0%, rgba(254,229,0,.44) 38%,
          rgba(254,229,0,.18) 60%, transparent 74%);
      filter: blur(2px);
      animation: orb-fb-pulse 4s ease-in-out infinite;
    }
    @keyframes orb-fb-pulse { 0%,100% { opacity:.7; transform:scale(1);} 50% { opacity:1; transform:scale(1.04);} }
    @media (prefers-reduced-motion: reduce){ .orb-container.orb-fallback { animation:none; } }
  `;
  function ensureOrbCSS() {
    if (typeof document === "undefined" || document.getElementById("orb-css")) return;
    const s = document.createElement("style");
    s.id = "orb-css";
    s.textContent = ORB_CSS;
    document.head.appendChild(s);
  }

  const vert = `
    precision highp float;
    attribute vec2 position;
    attribute vec2 uv;
    varying vec2 vUv;
    void main() { vUv = uv; gl_Position = vec4(position, 0.0, 1.0); }
  `;

  const frag = `
    precision highp float;
    uniform float iTime;
    uniform vec3 iResolution;
    uniform float hue;
    uniform float hover;
    uniform float rot;
    uniform float hoverIntensity;
    uniform vec3 backgroundColor;
    uniform float shadowStrength;
    uniform vec3 baseColor1;
    uniform vec3 baseColor2;
    uniform vec3 baseColor3;
    varying vec2 vUv;

    vec3 rgb2yiq(vec3 c){ float y=dot(c,vec3(0.299,0.587,0.114)); float i=dot(c,vec3(0.596,-0.274,-0.322)); float q=dot(c,vec3(0.211,-0.523,0.312)); return vec3(y,i,q); }
    vec3 yiq2rgb(vec3 c){ float r=c.x+0.956*c.y+0.621*c.z; float g=c.x-0.272*c.y-0.647*c.z; float b=c.x-1.106*c.y+1.703*c.z; return vec3(r,g,b); }
    vec3 adjustHue(vec3 color,float hueDeg){ float h=hueDeg*3.14159265/180.0; vec3 yiq=rgb2yiq(color); float ca=cos(h),sa=sin(h); float i=yiq.y*ca-yiq.z*sa; float q=yiq.y*sa+yiq.z*ca; yiq.y=i; yiq.z=q; return yiq2rgb(yiq); }
    vec3 hash33(vec3 p3){ p3=fract(p3*vec3(0.1031,0.11369,0.13787)); p3+=dot(p3,p3.yxz+19.19); return -1.0+2.0*fract(vec3(p3.x+p3.y,p3.x+p3.z,p3.y+p3.z)*p3.zyx); }
    float snoise3(vec3 p){ const float K1=0.333333333; const float K2=0.166666667; vec3 i=floor(p+(p.x+p.y+p.z)*K1); vec3 d0=p-(i-(i.x+i.y+i.z)*K2); vec3 e=step(vec3(0.0),d0-d0.yzx); vec3 i1=e*(1.0-e.zxy); vec3 i2=1.0-e.zxy*(1.0-e); vec3 d1=d0-(i1-K2); vec3 d2=d0-(i2-K1); vec3 d3=d0-0.5; vec4 h=max(0.6-vec4(dot(d0,d0),dot(d1,d1),dot(d2,d2),dot(d3,d3)),0.0); vec4 n=h*h*h*h*vec4(dot(d0,hash33(i)),dot(d1,hash33(i+i1)),dot(d2,hash33(i+i2)),dot(d3,hash33(i+1.0))); return dot(vec4(31.316),n); }
    vec4 extractAlpha(vec3 c){ float a=max(max(c.r,c.g),c.b); return vec4(c.rgb/(a+1e-5),a); }

    const float innerRadius = 0.6;
    const float noiseScale = 0.65;

    float light1(float i,float a,float d){ return i/(1.0+d*a); }
    float light2(float i,float a,float d){ return i/(1.0+d*d*a); }

    vec4 draw(vec2 uv){
      vec3 color1=adjustHue(baseColor1,hue);
      vec3 color2=adjustHue(baseColor2,hue);
      vec3 color3=adjustHue(baseColor3,hue);
      float ang=atan(uv.y,uv.x);
      float len=length(uv);
      float invLen=len>0.0?1.0/len:0.0;
      float bgL=dot(backgroundColor,vec3(0.299,0.587,0.114));
      float n0=snoise3(vec3(uv*noiseScale,iTime*0.5))*0.5+0.5;
      float r0=mix(mix(innerRadius,1.0,0.4),mix(innerRadius,1.0,0.6),n0);
      float d0=distance(uv,(r0*invLen)*uv);
      float v0=light1(1.0,10.0,d0);
      v0*=smoothstep(r0*1.05,r0,len);
      float innerFade=smoothstep(r0*0.8,r0*0.95,len);
      v0*=mix(innerFade,1.0,bgL*0.7);
      float cl=cos(ang+iTime*2.0)*0.5+0.5;
      float a=iTime*-1.0;
      vec2 pos=vec2(cos(a),sin(a))*r0;
      float d=distance(uv,pos);
      float v1=light2(1.5,5.0,d);
      v1*=light1(1.0,50.0,d0);
      float v2=smoothstep(1.0,mix(innerRadius,1.0,n0*0.5),len);
      float v3=smoothstep(innerRadius,mix(innerRadius,1.0,0.5),len);
      vec3 colBase=mix(color1,color2,cl);
      float fade=mix(1.0,0.1,bgL);
      vec3 darkCol=mix(color3,colBase,v0);
      darkCol=(darkCol+v1)*v2*v3;
      float outerHaloMask=smoothstep(r0-0.01,r0+0.10,len)*(1.0-smoothstep(r0+0.18,r0+0.52,len));
      float haloAttenuation=1.0-outerHaloMask*(1.0-shadowStrength);
      darkCol*=haloAttenuation;
      darkCol=clamp(darkCol,0.0,1.0);
      vec3 lightCol=(colBase+v1)*mix(1.0,v2*v3,fade);
      lightCol=mix(backgroundColor,lightCol,haloAttenuation);
      lightCol=mix(backgroundColor,lightCol,v0); lightCol=clamp(lightCol,0.0,1.0);
      vec3 finalCol=mix(darkCol,lightCol,bgL);
      return extractAlpha(finalCol);
    }

    vec4 mainImage(vec2 fragCoord){
      vec2 center=iResolution.xy*0.5;
      float size=min(iResolution.x,iResolution.y);
      vec2 uv=(fragCoord-center)/size*2.0;
      float s=sin(rot),c=cos(rot);
      uv=vec2(c*uv.x-s*uv.y,s*uv.x+c*uv.y);
      uv.x+=hover*hoverIntensity*0.1*sin(uv.y*10.0+iTime);
      uv.y+=hover*hoverIntensity*0.1*sin(uv.x*10.0+iTime);
      return draw(uv);
    }
    void main(){ vec2 fc=vUv*iResolution.xy; vec4 col=mainImage(fc); gl_FragColor=vec4(col.rgb*col.a,col.a); }
  `;

  function hexToVec3(Vec3, color) {
    if (typeof color === "string" && color.startsWith("#")) {
      return new Vec3(
        parseInt(color.slice(1, 3), 16) / 255,
        parseInt(color.slice(3, 5), 16) / 255,
        parseInt(color.slice(5, 7), 16) / 255
      );
    }
    const m = typeof color === "string" && color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
    if (m) return new Vec3(+m[1] / 255, +m[2] / 255, +m[3] / 255);
    return new Vec3(0, 0, 0);
  }

  function Orb({
    hue = 0, hoverIntensity = 0.2, rotateOnHover = true,
    forceHoverState = false, backgroundColor = "#000000", colors = null, shadowStrength = 1,
    size, className = "", style = {},
  }) {
    const ref = React.useRef(null);
    React.useEffect(() => { ensureOrbCSS(); }, []);

    React.useEffect(() => {
      const container = ref.current;
      if (!container) return;
      let disposed = false;
      let cleanup = () => {};

      dynamicImport(OGL_URL)
        .then((ogl) => {
          if (disposed || !container) return;
          cleanup = initOrb(ogl, container, { hue, hoverIntensity, rotateOnHover, forceHoverState, backgroundColor, colors, shadowStrength });
        })
        .catch(() => { if (!disposed && container) container.classList.add("orb-fallback"); });

      return () => { disposed = true; cleanup(); };
    }, [hue, hoverIntensity, rotateOnHover, forceHoverState, backgroundColor, colors, shadowStrength]);

    const dim = size != null ? (typeof size === "number" ? size + "px" : size) : undefined;
    return (
      <div ref={ref} className={"orb-container " + className} aria-hidden="true"
        style={dim ? { width: dim, height: dim, ...style } : style} />
    );
  }

  function initOrb(ogl, container, opts) {
    const { Renderer, Program, Mesh, Triangle, Vec3 } = ogl;
    const palette = Array.isArray(opts.colors) && opts.colors.length === 3
      ? opts.colors
      : ["#9C43FE", "#4CC2E9", "#101499"];
    let renderer;
    try {
      renderer = new Renderer({ alpha: true, premultipliedAlpha: false });
    } catch (e) {
      container.classList.add("orb-fallback");
      return () => {};
    }
    const gl = renderer.gl;
    gl.clearColor(0, 0, 0, 0);
    container.appendChild(gl.canvas);

    const geometry = new Triangle(gl);
    const program = new Program(gl, {
      vertex: vert, fragment: frag,
      uniforms: {
        iTime: { value: 0 },
        iResolution: { value: new Vec3(1, 1, 1) },
        hue: { value: opts.hue },
        hover: { value: 0 },
        rot: { value: 0 },
        hoverIntensity: { value: opts.hoverIntensity },
        backgroundColor: { value: hexToVec3(Vec3, opts.backgroundColor) },
        shadowStrength: { value: opts.shadowStrength ?? 1 },
        baseColor1: { value: hexToVec3(Vec3, palette[0]) },
        baseColor2: { value: hexToVec3(Vec3, palette[1]) },
        baseColor3: { value: hexToVec3(Vec3, palette[2]) },
      },
    });
    const mesh = new Mesh(gl, { geometry, program });

    function resize() {
      // Perf: cap DPR and render at reduced internal resolution — the orb is
      // soft/blurred so a lower buffer is visually indistinguishable but much cheaper.
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5) * 0.8;
      const w = container.clientWidth || 1;
      const h = container.clientHeight || 1;
      renderer.setSize(w * dpr, h * dpr);
      gl.canvas.style.width = w + "px";
      gl.canvas.style.height = h + "px";
      program.uniforms.iResolution.value.set(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
    }
    window.addEventListener("resize", resize);
    let ro;
    if (typeof ResizeObserver !== "undefined") { ro = new ResizeObserver(resize); ro.observe(container); }
    resize();

    let targetHover = 0, currentRot = 0, lastTime = 0;
    const rotationSpeed = 0.3;
    const onMove = (e) => {
      const rect = container.getBoundingClientRect();
      const size = Math.min(rect.width, rect.height);
      const uvX = ((e.clientX - rect.left - rect.width / 2) / size) * 2;
      const uvY = ((e.clientY - rect.top - rect.height / 2) / size) * 2;
      targetHover = Math.sqrt(uvX * uvX + uvY * uvY) < 0.8 ? 1 : 0;
    };
    const onLeave = () => { targetHover = 0; };
    container.addEventListener("mousemove", onMove);
    container.addEventListener("mouseleave", onLeave);

    // Perf: only render while the orb is actually visible AND the tab is focused.
    // When scrolled offscreen or the tab is backgrounded, the loop fully idles (0 GPU).
    let onScreen = true;
    let io;
    if (typeof IntersectionObserver !== "undefined") {
      io = new IntersectionObserver((entries) => { onScreen = entries[0].isIntersecting; }, { threshold: 0.01 });
      io.observe(container);
    }
    const isAwake = () => onScreen && !document.hidden;

    let rafId, lastRender = 0;
    const FRAME_MS = 33; // ~30fps cap — plenty for a soft ambient orb
    const update = (t) => {
      rafId = requestAnimationFrame(update);
      if (!isAwake()) { lastTime = t; return; }   // idle: skip work entirely
      if (t - lastRender < FRAME_MS) return;
      const dt = (t - lastTime) * 0.001; lastTime = t; lastRender = t;
      program.uniforms.iTime.value = t * 0.001;
      const eff = opts.forceHoverState ? 1 : targetHover;
      program.uniforms.hover.value += (eff - program.uniforms.hover.value) * 0.1;
      if (opts.rotateOnHover && eff > 0.5) currentRot += dt * rotationSpeed;
      program.uniforms.rot.value = currentRot;
      renderer.render({ scene: mesh });
    };
    rafId = requestAnimationFrame(update);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", resize);
      if (ro) ro.disconnect();
      if (io) io.disconnect();
      container.removeEventListener("mousemove", onMove);
      container.removeEventListener("mouseleave", onLeave);
      try { container.removeChild(gl.canvas); } catch (e) {}
      gl.getExtension("WEBGL_lose_context") && gl.getExtension("WEBGL_lose_context").loseContext();
    };
  }

  if (typeof window !== "undefined") window.Orb = Orb;
})();
